// Bills and payments in the browser: one indexBills() (shared/bills.ts) over the synced tables, rebuilt only when one
// of them changes and shared by every component that asks; matches, states and the due board are worked out on it
// (and cached on it), so opening a bill or switching tabs costs nothing. Plus who may pay and Wise's state here.
import { type BillData, type BillIndex, indexBills } from "../../../shared/bills.ts";
import { planToday } from "../../../shared/day.ts";
import { me } from "./session.ts";
import { useRows } from "./sync.ts";

/** HHL's date of a moment (Texas), like the server's. */
export const dayOf = (ts: string) => planToday(new Date(ts));

let last: { inputs: unknown[]; index: BillIndex } | null = null;

export function useBillIndex(): BillIndex {
  const tables = {
    bills: useRows("bill"),
    billLines: useRows("bill_line"),
    payments: useRows("payment"),
    payAccounts: useRows("pay_account"), // Which card ---
    allocations: useRows("payment_allocation"),
    vendors: useRows("vendor"),
    recipients: useRows("vendor_recipient"),
    pos: useRows("po"),
    poLines: useRows("po_line"),
    receipts: useRows("receipt"),
    costs: useRows("production_cost"),
    tooling: useRows("tooling_item"),
    runs: useRows("production_run"),
    attachments: useRows("attachment"),
    makeOrders: useRows("make_order"), // Pre-sales --- the backers' units a make PO line has delivered
  };
  const today = planToday();
  const inputs = [today, ...Object.values(tables)];
  if (!last || last.inputs.some((x, i) => x !== inputs[i])) last = { inputs, index: indexBills(tables as unknown as BillData, today) };
  return last.index;
}

/** Whether the signed-in person may make payments (app_user.can_pay, synced, so a change shows at once). */
export function useCanPay() {
  const users = useRows("app_user");
  const id = me()?.id;
  const u = users.find((x) => x.id === id);
  return !!u?.can_pay && u.role !== "receiving" && u.active;
}

/** Wise here: on (a token or the fake), its environment, and whether the token can create transfers. */
export function wiseFeature() {
  const w = me()?.wise;
  return { enabled: !!w?.enabled, fake: !!w?.fake, env: w?.env ?? "sandbox", missing: w?.missing ?? [] };
}
export function useWiseConnection() {
  return useRows("wise_connection").find((c) => c.id === 1) ?? null;
}

/** "$1,234.56" or "¥…": money in a currency, always two decimals. */
export const amount = (n: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/** Totals in USD plus any other currency: "$1,200.00 + 2,100.00 CNY". */
export const totalsText = (t: { usd: number; other: Record<string, number> }) =>
  [amount(t.usd), ...Object.entries(t.other).map(([cur, n]) => amount(n, cur))].join(" + ");

/** POST JSON to an /api route; throws the server's message. */
export async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status})`);
  return data;
}
